import { useRouter } from 'next/router'
import React, { useState } from 'react'

import clsx from 'clsx'
import { FormattedMessage } from 'react-intl'

import { DEFAULT_CURRENCY } from '@/lib/constants'
import { AnnualForecast, Device, OrderEstimate } from '@/lib/types'
import { formatCurrency } from '@/lib/utils'

import { useHostingMessages } from '@/hooks/useHostingMessage'

import Icon from '@/components/Icon'
import Tooltip from '@/components/Tooltip'
import { ProfitTooltip } from '@/components/features/shop/ProfitTooltip'
import RowSkeleton from '@/components/skeletons/RowSkeleton'

export const FundsFlowInfoPerTH = ({
  estimate,
  device,
  isLoading
}: {
  estimate?: OrderEstimate
  device: Device
  isLoading: boolean
}) => {
  const { currency: deviceCurrency, estimateAnnualProfit } = device
  const { monthlyEstimate, dailyEstimate } = estimate || {}

  const currency = deviceCurrency || DEFAULT_CURRENCY

  const { incomeMessage, hostingMessage } = useHostingMessages({
    currency,
    dailyEstimate,
    monthlyEstimate
  })

  return (
    <>
      <div className="flex flex-col gap-1 tablet:justify-between">
        <div className="flex flex-col gap-0.5">
          <ProfitPerYear
            {...{
              currency,
              isLoading,
              estimateAnnualProfit,
              annualForecast: estimate?.annualForecast
            }}
          />
        </div>
        <div className="mt-1 flex gap-3 max-tablet:flex-col">
          {[incomeMessage, hostingMessage].map((message, index) => (
            <span
              className={clsx(
                'inline-flex max-w-max items-center justify-center rounded-md',
                'bg-slate-100 px-2.5 py-1 text-x-sm leading-x-sm'
              )}
              key={index}
            >
              {message}
            </span>
          ))}
        </div>
      </div>
    </>
  )
}

export const ProfitPerYear = ({
  estimateAnnualProfit,
  annualForecast,
  currency,
  isLoading
}: {
  estimateAnnualProfit: number
  annualForecast?: AnnualForecast
  currency: string
  isLoading: boolean
}) => {
  const { locale } = useRouter()

  const [isProfitVisible, setIsProfitVisible] = useState(false)

  return (
    <div className="relative mt-auto font-medium">
      <div className="flex items-center gap-2">
        {isLoading ? (
          <RowSkeleton className="h-5 w-40" />
        ) : (
          <p className="mt-auto text-x-base leading-x-base">
            <FormattedMessage
              {...{
                id: 'dialog.device.purchase.profit',
                defaultMessage: 'Прибыль',
                values: { profit: estimateAnnualProfit }
              }}
            />{' '}
            ~{' '}
            {annualForecast &&
              formatCurrency(annualForecast?.['100k'].total, currency, locale)}
          </p>
        )}
        <Tooltip
          className="hidden tablet:block"
          contentComponent={<ProfitTooltip {...{ annualForecast, currency }} />}
        >
          <button onClick={() => setIsProfitVisible((prev) => !prev)}>
            <Icon
              className="inline h-4 w-4 flex-none text-secondary-500"
              name="calculator-16"
            />
          </button>
        </Tooltip>
      </div>
      {isProfitVisible && (
        <ProfitTooltip
          className="mt-1 font-normal tablet:hidden"
          {...{ annualForecast, currency }}
        />
      )}
    </div>
  )
}
